import { DataView } from "@zebrabi/matrix-data";
import type { RowData, SheetData } from "x-data-spreadsheet";
import { looksLikeCommentHeader, MeasureRoles } from "@zebrabi/data-helpers/fieldAssignment";
import { getTablesMetadata, setMeasureRoleByName } from "./tablesMetadata";
import { parseRowHierarchyDataView } from "./hierarchyParsing";
import { shapeRows } from "./groupByColumn";

/**
 * PowerPoint sheet shaped in memory (docs/group-by-column.md): the chosen Category columns (any order) become the
 * row hierarchy, rows with the same path merge, and a "group by" column pivots the rows into category rows × its
 * values, measures summed per cell. Column arguments are absolute column indexes in the sheet; `groupByColumn` and
 * `commentColumn` are -1 when there is none. The flat sheet stays the editor's content; only the DataView is shaped.
 */
export function parseShapedDataView(
    data: SheetData,
    categoryColumns: number[],
    measureColumns: number[],
    groupByColumn: number,
    commentColumn: number,
    /** Measures that carry text (text measures by the user's choice), absolute column indexes */
    textColumns: number[] = [],
    /** The comment column is Comments by the user's choice, not by header */
    commentByChoice = false
): DataView {
    const header = data.rows[0]?.cells ?? {};
    const headerText = (column: number) => header[column]?.text ?? "";
    const categoryFields = categoryColumns.map((column) => headerText(column) || "Category");
    const measureFields = measureColumns.map(headerText);
    const columnFields = groupByColumn >= 0 ? [headerText(groupByColumn)] : [];

    const dataView: DataView = {
        metadata: { columns: [] },
        matrix: null,
        rowFields: categoryFields,
        columnFields,
        valueFields: measureFields,
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: categoryFields,
        columnNames: columnFields,
        valueNames: measureFields,
    };

    const cellText = (row: RowData, column: number): string => {
        const cell = row?.cells?.[column];
        if (!cell) {
            return "";
        }
        const text = cell.text ?? "";
        return text[0] === "=" ? (cell.calculatedValue ?? "") : text;
    };
    const rows: RowData[] = Object.keys(data.rows)
        .filter((key) => key !== "len")
        .map(Number)
        .filter((index) => index > 0)
        .sort((a, b) => a - b)
        .map((index) => data.rows[index])
        // rows the user never typed into (or cleared) carry nothing to group
        .filter((row) => Object.keys(row?.cells ?? {}).some((column) => cellText(row, Number(column)) !== ""));

    const shaped = shapeRows({
        rowLabels: rows.map((row) => categoryColumns.map((column) => cellText(row, column))),
        values: rows.map((row) => measureColumns.map((column) => cellText(row, column))),
        groupCells: groupByColumn >= 0 ? rows.map((row) => cellText(row, groupByColumn)) : undefined,
        commentIndex: measureColumns.indexOf(commentColumn),
        textIndexes: textColumns.map((column) => measureColumns.indexOf(column)).filter((index) => index >= 0),
    });

    getTablesMetadata(dataView, false, commentColumn >= 0);
    if (commentByChoice && commentColumn >= 0) {
        // the auto-assignment only knows comments by header
        setMeasureRoleByName(dataView, headerText(commentColumn), MeasureRoles.Comments);
    }
    // the range parsers take a flat list of column labels where their signature says any[][]
    const columnLabels = groupByColumn >= 0 ? shaped.groups : measureFields;
    parseRowHierarchyDataView(dataView, shaped.rowLabels, <unknown[][]>(<unknown>columnLabels), shaped.values, null);
    return dataView;
}

/**
 * Tables with "Columns (n)" in Group (a cross table from a flat sheet): rows merged by category path (sums), then
 * every measure column in use is a group of its own with one "Values" measure. The same matrix an Excel cross table
 * produces; a comment column is not carried (as in the legacy wide sheet).
 */
export function parseColumnsAsGroupsDataView(data: SheetData, categoryColumns: number[], measureColumns: number[]): DataView {
    const header = data.rows[0]?.cells ?? {};
    const headerText = (column: number) => header[column]?.text ?? "";
    const categoryFields = categoryColumns.map((column) => headerText(column) || "Category");
    const groupHeadings = measureColumns.map(headerText);

    const dataView: DataView = {
        metadata: { columns: [] },
        matrix: null,
        rowFields: categoryFields,
        columnFields: ["Group"],
        valueFields: ["Values"],
        rowGrandTotal: false,
        columnGrandTotal: false,
        rowNames: categoryFields,
        columnNames: ["Group"],
        valueNames: ["Values"],
        columnsAsGroups: true,
    };

    const cellText = (row: RowData, column: number): string => {
        const cell = row?.cells?.[column];
        if (!cell) {
            return "";
        }
        const text = cell.text ?? "";
        return text[0] === "=" ? (cell.calculatedValue ?? "") : text;
    };
    const rows: RowData[] = Object.keys(data.rows)
        .filter((key) => key !== "len")
        .map(Number)
        .filter((index) => index > 0)
        .sort((a, b) => a - b)
        .map((index) => data.rows[index])
        .filter((row) => Object.keys(row?.cells ?? {}).some((column) => cellText(row, Number(column)) !== ""));

    const shaped = shapeRows({
        rowLabels: rows.map((row) => categoryColumns.map((column) => cellText(row, column))),
        values: rows.map((row) => measureColumns.map((column) => cellText(row, column))),
    });

    getTablesMetadata(dataView, false, false);
    // one value per group and row: the merged rows already come as rows × (one group × measures)
    parseRowHierarchyDataView(dataView, shaped.rowLabels, <unknown[][]>(<unknown>groupHeadings), shaped.values, null);
    return dataView;
}

// PPT
export function tryParseHierarchicalDataView(data: SheetData, categoryColumns: number): DataView {
    let matrixDataView: DataView = null;
    try {
        matrixDataView = initDataView(data, categoryColumns);

        let rowLabelsValues: string[][] = getCategorylabels(data, categoryColumns);
        let columnLabelsValues = <any[]>matrixDataView.valueFields;
        let dataValues: unknown[][] = getDataValues(data, categoryColumns);

        const isPivotTable = false;
        const hasComments = columnLabelsValues.some((c) => looksLikeCommentHeader(c));

        getTablesMetadata(matrixDataView, isPivotTable, hasComments);

        //console.log(rowLabelsValues, columnLabelsValues, dataValues)
        parseRowHierarchyDataView(matrixDataView, rowLabelsValues, columnLabelsValues, dataValues, null);
        //TOcheck use: dv = dh.parseRangeDataView(dv, null, null, null, null, rowLabelsValues, columnLabelsValues, dataValues, null, null);
        // the full sheet is stored by xSpreadsheetToMatrixData before any parse; storing `data` here would overwrite it
        // with the sheet minus the columns unticked in the Data fields pane
    } catch (e) {
        console.log("tryParseHierarchicalDataView:", e);
        matrixDataView = null;
    }

    return matrixDataView;
}

function initDataView(sheetData: SheetData, categoryColumns: number): DataView {
    let categoryFields = [];
    const groupFields = [];
    let measureFields = [];
    const rowGrandTotal = false;
    const columnGrandTotal = false;

    const categoryColumnsHeaders = Object.values(sheetData.rows[0]?.cells || []) //
        .filter((_cell, index) => index < categoryColumns)
        .filter((cell) => !!cell?.text)
        .map((cell) => cell?.text);

    const measureColumnsHeaders = Object.values(sheetData.rows[0]?.cells || []) // First row without the first cell
        .filter((_cell, index) => index > categoryColumns - 1)
        .filter((cell) => !!cell?.text)
        .map((cell) => cell?.text);

    categoryFields = categoryColumnsHeaders;
    measureFields = measureColumnsHeaders;

    const dataView: DataView = {
        metadata: { columns: [] },
        matrix: null,
        rowFields: categoryFields,
        columnFields: groupFields,
        valueFields: measureFields,
        rowGrandTotal: rowGrandTotal,
        columnGrandTotal: columnGrandTotal,
        rowNames: categoryFields,
        columnNames: groupFields,
        valueNames: measureFields,
    };
    return dataView;
}

function getCategorylabels(data: SheetData, categoryColumns: number): string[][] {
    const categoryLabels: string[][] = [];

    const sourceDataRows = Object.keys(data.rows)
        .filter((key, i) => i > 0) // skip first row
        .map((key) => {
            return data.rows[+key];
        });

    let currentHierarchy: string[] = [];

    sourceDataRows.forEach((row) => {
        let rowLabels: string[] = [];
        for (let i = 0; i < categoryColumns; i++) {
            const cell = row.cells[i];

            if (currentHierarchy?.[i] === cell.text) {
                rowLabels.push("");
            } else {
                rowLabels.push(cell.text);

                if (currentHierarchy?.[i] !== undefined) {
                    currentHierarchy[i] = cell.text;
                } else {
                    currentHierarchy.push(cell.text);
                }
            }
        }

        categoryLabels.push(rowLabels);
    });
    return categoryLabels;
}

function getDataValues(data: SheetData, categoryColumns: number): unknown[][] {
    const measureValues: unknown[][] = [];

    const sourceDataRows = Object.keys(data.rows)
        .filter((key, i) => i > 0) // skip first row
        .map((key) => {
            return data.rows[+key];
        });

    sourceDataRows.forEach((row) => {
        let rowValues: unknown[] = [];
        Object.keys(row.cells).forEach((key) => {
            const index = Number(key);
            if (index >= categoryColumns) {
                const cell = row?.cells[index];
                rowValues.push(typeof cell?.text === "number" ? +cell?.text : (cell.text ?? "")); //check?: handle numbers and/or string for comments
            }
        });
        measureValues.push(rowValues);
    });
    return measureValues;
}
